import { useEffect, useRef } from 'react'

import { extractBuildIdFromHtml } from '@/utils'

import { useBuildVersionStore } from './build-version.store'

const POLL_INTERVAL_MS = 5 * 60 * 1000

const checkForNewBuild = async () => {
  try {
    // Note: `cache: 'no-store'` bypasses the browser HTTP cache. `index.html` is currently
    // uncached at the Cloudflare edge too (verified live against production), which is what
    // makes reusing it here safer than standing up a separate manifest endpoint — see the
    // stale-client investigation behind EL-13298.
    const response = await fetch('/', { cache: 'no-store' })
    if (!response.ok) return

    const html = await response.text()
    const buildId = extractBuildIdFromHtml(html)
    if (buildId) {
      useBuildVersionStore.getState().setLatestBuildId(buildId)
    }
  } catch {
    // Note: network hiccups (including the brief window mid-deploy where the origin has no
    // files at all) aren't a staleness signal — just try again next interval.
  }
}

/**
 * Tier 0 — detection only. Polls `index.html` for the current build id while the tab is
 * visible; never runs while hidden. Mount once, globally (see `GlobalListeners.jsx`).
 */
export const useBuildVersionPoller = () => {
  const intervalRef = useRef<number | null>(null)

  useEffect(() => {
    const startPolling = () => {
      if (intervalRef.current) return
      checkForNewBuild()
      intervalRef.current = window.setInterval(checkForNewBuild, POLL_INTERVAL_MS)
    }

    const stopPolling = () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startPolling()
      } else {
        stopPolling()
      }
    }

    if (document.visibilityState === 'visible') {
      startPolling()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      stopPolling()
    }
  }, [])
}
